import React, { useEffect, useRef } from 'react';
import { Tabs, Tab, TabList } from 'react-tabs';
import TabIndicator from './TabIndicator';
import { css } from '@emotion/css';
import { dark_sky_blue, ocean_blue, silver_two, blue_white, dark_grey_blue } from '../../constants/colors';
import { breakpoint_small } from '../../constants/breakpoints';
import SparkContainer from '../../hooks/SparkContainer';
import useIsMobile from '../../hooks/UseIsMobile';

interface TabbedLayoutProps {
    tabs: string[]
    top?: boolean
    selectedIndex?: number
    forceRenderTabPanel?: boolean
    onTabSelected?: (index: number, last: number) => void
}

const TabbedLayout: React.FC<TabbedLayoutProps> = (props) => {
    const sparkContainer = SparkContainer.useContainer();
    const inSpark = sparkContainer.inSpark;
    const inMobile = useIsMobile();
    const tabListRef = useRef<HTMLDivElement | null>(null);

    const tabListStyle = css`
        ${tabStripStyle};
        ${(inSpark && inMobile) && sparkMobileTabStripStyle};
        ${inMobile && mobileTabStripStyle};
    `;

    const indicatorGradient = `linear-gradient(267deg, ${dark_sky_blue}, ${ocean_blue});`;

    // Scroll selected tab into view on mobile (when first loaded, it goes to activity)
    useEffect(() => {
        if (inMobile && tabListRef.current && props.selectedIndex !== undefined) {
            const tabListElement = tabListRef.current.querySelector('ul');
            if (tabListElement) {
                const selectedTab = tabListElement.children[props.selectedIndex] as HTMLElement;
                if (selectedTab) {
                    selectedTab.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'center'
                    });
                }
            }
        }
    }, [props.selectedIndex, inMobile]);

    useEffect(() => {
        if (inMobile && tabListRef.current) {
            const tabListElement = tabListRef.current.querySelector('ul');
            if (tabListElement) {
                tabListElement.scrollLeft = 0;
            }
        }
    }, [inMobile]);

    function renderTab(name: string) {
        return (
            <Tab selectedClassName={activeTabStyle} className={`${tabStyle} ac-tab-${name}`} key={name}>
                <span>{name}</span>
                <TabIndicator color={indicatorGradient} className={tabIndicatorStyle} />
            </Tab>
        );
    }
    return (
        <Tabs forceRenderTabPanel={props.forceRenderTabPanel} className={props.top ? topTabLayoutStyle : tablayoutStyle} defaultIndex={props.selectedIndex} onSelect={props.onTabSelected}>
            <div ref={tabListRef}>
                <TabList className={`${tabListStyle}`}>
                    {props.tabs.map(renderTab)}
                </TabList>
            </div>
            {props.children}
        </Tabs>
    );
}

const sparkMobileTabStripStyle = css`
    height: 44px;
    align-items: center;
`;

const mobileTabStripStyle = css`
    height: 50px;
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    justify-content: flex-start;
    &::-webkit-scrollbar {
        display: none;
    }
    padding-bottom: 3px;
    padding-left: 0;
    margin-left: 0;
`;

const tabStripStyle = css`
    display: flex;
    justify-content: center;
    flex-direction: row;
    height: 80px;
    list-style: none;
    border-bottom: solid 1px ${silver_two};
`;

const tabStyle = css`
    display: flex;
    flex-direction: column;
    width: 22%;
    color: black;
    text-align: center;
    cursor: pointer;
    span {
        flex-grow: 1;
        line-height: 80px;
        font-family: Muli;
        font-size: 18px;
        font-weight: normal;
        font-style: normal;
        letter-spacing: 0.5px;
        color: ${dark_grey_blue};
    }
    div {
        display: none;
        flex-grow: 0;
        flex-shrink: 0;
    }
    ${breakpoint_small} {
        min-width: 120px;
        width: auto;
        flex-shrink: 0;
        padding: 0 16px;
        span {
            font-size: 14px;
            text-align: center;
            line-height: 50px;
            white-space: nowrap;
        }
    }
`;

const activeTabStyle = css`
    color: ${ocean_blue};
    div {
        display: block;
    }
`;

const tablayoutStyle = css`
margin-top: 80px;
background: ${blue_white};
`;

const topTabLayoutStyle = css`
    margin-top: 0;
    background: ${blue_white};
`;

const tabIndicatorStyle = css`
    margin: -2px auto 0 auto;
`;

export default TabbedLayout;
